import React, { useState, useRef } from 'react'
import ReactDOM from 'react-dom'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { Eye, EyeOff, StickyNote, Mail, Lock, Monitor, AlertTriangle, X } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { useLocalMode } from '../../contexts/LocalModeContext'
import { useTheme } from '../../contexts/ThemeContext'
import { ButtonSpinner } from '../../components/common/LoadingSpinner'
import { apiUtils } from '../../services/api'
import { useEffect } from 'react'
import noteSparkLogo from '../../assets/notespark.png'

/**
 * Global modal state that persists across LoginPage component re-mounts
 * This is needed because the component gets unmounted/re-mounted during login
 */
const getModalState = () => ({
  showInvalidModal: window._loginPageModalState?.showInvalidModal || false,
  invalidMsg: window._loginPageModalState?.invalidMsg || '',
  showMaintenanceModal: window._loginPageModalState?.showMaintenanceModal || false,
  maintenanceMsg: window._loginPageModalState?.maintenanceMsg || ''
})

const setModalState = (updates) => {
  if (!window._loginPageModalState) {
    window._loginPageModalState = {}
  }
  window._loginPageModalState = { ...window._loginPageModalState, ...updates }
}

/**
 * Login Page Component
 * 
 * Provides user authentication with email and password.
 * Includes form validation, loading states, error handling, and Local Mode option.
 */
const LoginPage = () => {
  const { login } = useAuth()
  const { enableLocalMode } = useLocalMode()
  const { currentTheme, setTheme } = useTheme()
  const navigate = useNavigate()
  const location = useLocation()
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [showLocalModeModal, setShowLocalModeModal] = useState(false)
  
  // Force re-render when modal state changes
  const [, forceUpdate] = useState({})
  const forceRender = () => forceUpdate({})
  
  // For pre-filling email in reset flow
  const [emailValue, setEmailValue] = useState('')

  // Force light mode for login page - captive portal should never use dark mode
  useEffect(() => {
    const previousTheme = currentTheme
    if (currentTheme !== 'light') {
      setTheme('light')
    }
    return () => {
      // Restore previous theme when leaving the page
      if (previousTheme !== 'light') {
        setTheme(previousTheme)
      }
    }
  }, [])

  // Debug: Track component mounting/unmounting  
  useEffect(() => {
    console.log('🏗️ LoginPage: Component MOUNTED')
    console.log('🏗️ LoginPage: Current modal state:', getModalState())
    return () => {
      console.log('💀 LoginPage: Component UNMOUNTED')
      console.log('💀 LoginPage: Modal state at unmount:', getModalState())
      // Don't clear state here - component might be re-mounting during login
    }
  }, [])

  useEffect(() => {
    const handler = (e) => {
      const msg = e?.detail?.message || 'Invalid email or password'
      console.log('🔔 LoginPage: Received loginInvalidCredentials event')
      console.log('🔔 LoginPage: Setting modal state to true')
      setModalState({ showInvalidModal: true, invalidMsg: `${msg}. If you forgot your password, you can reset it now.` })
      forceRender() // Trigger re-render
      // Add a small delay to ensure state is set
      setTimeout(() => {
        console.log('🔔 LoginPage: Modal state after timeout should be true')
      }, 100)
    }
    console.log('📡 LoginPage: Subscribing to loginInvalidCredentials events')
    window.addEventListener('loginInvalidCredentials', handler)
    return () => window.removeEventListener('loginInvalidCredentials', handler)
  }, [])

  // Listen for maintenance modal events
  useEffect(() => {
    const handler = (e) => {
      const message = e?.detail?.message || 'Sorry, the application is under maintenance right now. You can use the offline mode for now.'
      console.log('🚧 LoginPage: Received showMaintenanceModal event')
      setModalState({ 
        showMaintenanceModal: true, 
        maintenanceMsg: message 
      })
      forceRender() // Trigger re-render
    }
    console.log('📡 LoginPage: Subscribing to showMaintenanceModal events')
    window.addEventListener('showMaintenanceModal', handler)
    return () => window.removeEventListener('showMaintenanceModal', handler)
  }, [])

  const from = location.state?.from?.pathname || '/dashboard'

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    clearErrors
  } = useForm({
    defaultValues: {
      email: '',
      password: ''
    }
  })

  const onSubmit = async (data) => {
    try {
      setIsLoading(true)
      clearErrors()

      const result = await login(data.email, data.password)
      
      if (result.success) {
        // Clear modal state on successful login
        setModalState({ showInvalidModal: false, invalidMsg: '' })
        
        // Check if user needs onboarding
        if (result.needsOnboarding) {
          navigate('/onboarding', { replace: true })
        } else {
          // Navigate to intended destination or dashboard
          navigate(from, { replace: true })
        }
      } else {
        // Handle login failure
        
        // Check for maintenance mode first
        if (result.isMaintenanceMode || result.statusCode === 503) {
          console.log('🚧 LoginPage: Detected maintenance mode, showing modal')
          setModalState({ 
            showMaintenanceModal: true, 
            maintenanceMsg: result.error || 'Sorry, the application is under maintenance right now. You can use the offline mode for now.' 
          })
          forceRender() // Trigger re-render
          return
        }
        
        if (result.invalidCredentials) {
          console.log('🧭 LoginPage: Opening invalid credentials modal (flag path)')
          setModalState({ showInvalidModal: true, invalidMsg: 'Invalid email or password. If you forgot your password, you can reset it now.' })
          setEmailValue(data.email || '')
          // Use setTimeout to avoid React batching/timing issues
          setTimeout(() => {
            setModalState({ showInvalidModal: true, invalidMsg: 'Invalid email or password. If you forgot your password, you can reset it now.' })
            forceRender() // Trigger re-render
          }, 0)
        } else {
          const lower = (result.error || '').toLowerCase()
          if (lower.includes('invalid')) {
            console.log('🧭 LoginPage: Opening invalid credentials modal (message path)')
            setModalState({ showInvalidModal: true, invalidMsg: 'Invalid email or password. If you forgot your password, you can reset it now.' })
            setEmailValue(data.email || '')
            // Use setTimeout to avoid React batching/timing issues
            setTimeout(() => {
              setModalState({ showInvalidModal: true, invalidMsg: 'Invalid email or password. If you forgot your password, you can reset it now.' })
              forceRender() // Trigger re-render
            }, 0)
          } else if (lower.includes('email')) {
            setError('email', { message: result.error })
          } else if (lower.includes('password')) {
            setError('password', { message: result.error })
          } else if (result.statusCode === 401) {
            console.log('🧭 LoginPage: Opening invalid credentials modal (status path)')
            setModalState({ showInvalidModal: true, invalidMsg: 'Invalid email or password. If you forgot your password, you can reset it now.' })
            setEmailValue(data.email || '')
            // Use setTimeout to avoid React batching/timing issues
            setTimeout(() => {
              setModalState({ showInvalidModal: true, invalidMsg: 'Invalid email or password. If you forgot your password, you can reset it now.' })
              forceRender() // Trigger re-render
            }, 0)
          } else {
            setError('root', { message: result.error || 'Login failed' })
          }
        }
      }
    } catch (error) {
      // Check for maintenance/network errors first
      if (error?.code === 'ERR_CONNECTION_REFUSED' || 
          error?.message?.includes('ERR_CONNECTION_REFUSED') ||
          error?.message?.includes('net::ERR_CONNECTION_REFUSED') ||
          error?.message?.includes('Network Error') ||
          error?.code === 'ERR_NETWORK') {
        console.log('🚧 LoginPage: Network error detected in catch, showing maintenance modal')
        setModalState({ 
          showMaintenanceModal: true, 
          maintenanceMsg: 'Sorry, the application is under maintenance right now. You can use the offline mode for now.' 
        })
        forceRender() // Trigger re-render
        return
      }
      
      // If anything else throws unexpectedly, still show graceful modal
      console.log('⚠️ LoginPage: Unexpected error in onSubmit, opening modal as fallback', error)
      setModalState({ showInvalidModal: true, invalidMsg: 'Invalid email or password. If you forgot your password, you can reset it now.' })
      // Use setTimeout to avoid React batching/timing issues
      setTimeout(() => {
        setModalState({ showInvalidModal: true, invalidMsg: 'Invalid email or password. If you forgot your password, you can reset it now.' })
        forceRender() // Trigger re-render
      }, 0)
    } finally {
      setIsLoading(false)
    }
  }
  
  // Invalid Credentials Modal Component (now inline to access parent scope)
  const invalidCredentialsModal = getModalState().showInvalidModal ? ReactDOM.createPortal(
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      style={{ zIndex: 9999 }}  // Force high z-index
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          setModalState({ showInvalidModal: false })
          forceRender() // Trigger re-render
        }
      }}
    >
      <div className="bg-white rounded-lg max-w-lg w-full shadow-2xl">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <AlertTriangle className="h-7 w-7 text-red-500 mr-3" />
            <h2 className="text-xl font-semibold text-gray-900">Sign in failed</h2>
          </div>
          <button onClick={() => {
            setModalState({ showInvalidModal: false })
            forceRender() // Trigger re-render
          }} className="text-gray-400 hover:text-gray-600">
            <X className="h-6 w-6" />
          </button>
        </div>
        <div className="p-6 space-y-3">
          <p className="text-gray-800">{getModalState().invalidMsg || 'Invalid email or password.'}</p>
          <p className="text-gray-600 text-sm">If you forgot your password, you can reset it using the link below.</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 p-6 border-t border-gray-200">
          <button
            onClick={() => {
              setModalState({ showInvalidModal: false })
              forceRender() // Trigger re-render
            }}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Try again
          </button>
          <button
            onClick={() => navigate('/forgot-password', { state: { email: emailValue } })}
            className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
          >
            Reset password
          </button>
        </div>
      </div>
    </div>,
    document.body
  ) : null

  const handleLocalModeClick = () => {
    setShowLocalModeModal(true)
  }

  const handleProceedWithLocalMode = async () => {
    try {
      setIsLoading(true)
      const success = await enableLocalMode()
      
      if (success) {
        setShowLocalModeModal(false)
        navigate('/notes', { replace: true })
      }
    } catch (error) {
      console.error('Failed to enable local mode:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const LocalModeModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200">
          <div className="flex items-center">
            <Monitor className="h-6 w-6 sm:h-8 sm:w-8 text-orange-500 mr-2 sm:mr-3" />
            <h2 className="text-lg sm:text-2xl font-bold text-gray-900">Use Local Mode</h2>
          </div>
          <button
            onClick={() => setShowLocalModeModal(false)}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Modal Content */}
        <div className="p-4 sm:p-6">
          {/* Main Message */}
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-100 rounded-full mb-4">
              <Monitor className="h-8 w-8 text-orange-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Start Using Immediately</h3>
            <p className="text-gray-600">
              No account needed. Your notes will be stored locally in this browser only.
            </p>
          </div>

          {/* Key Points */}
          <div className="space-y-3 mb-6">
            <div className="flex items-center text-sm">
              <span className="inline-flex items-center justify-center w-6 h-6 bg-green-100 rounded-full mr-3">
                <span className="text-green-600 text-xs">✓</span>
              </span>
              <span className="text-gray-700">Works offline, but no AI features</span>
            </div>
            <div className="flex items-center text-sm">
              <span className="inline-flex items-center justify-center w-6 h-6 bg-red-100 rounded-full mr-3">
                <span className="text-red-600 text-xs">⚠</span>
              </span>
              <span className="text-gray-700">Notes lost if browser data is cleared</span>
            </div>
            <div className="flex items-center text-sm">
              <span className="inline-flex items-center justify-center w-6 h-6 bg-blue-100 rounded-full mr-3">
                <span className="text-blue-600 text-xs">i</span>
              </span>
              <span className="text-gray-700">Single device only, no cloud sync</span>
            </div>
          </div>

          {/* Recommendation */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
            <p className="text-blue-700 text-sm">
              💡 <strong>Tip:</strong> Create an account later for cloud storage and device sync
            </p>
          </div>
        </div>

        {/* Modal Actions */}
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 p-4 sm:p-6 border-t border-gray-200">
          <button
            onClick={() => setShowLocalModeModal(false)}
            className="flex-1 px-4 py-2.5 sm:py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors text-sm sm:text-base"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            onClick={handleProceedWithLocalMode}
            disabled={isLoading}
            className="flex-1 px-4 py-2.5 sm:py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors disabled:opacity-50 flex items-center justify-center text-sm sm:text-base"
          >
            {isLoading ? (
              <>
                <ButtonSpinner className="mr-2" />
                Setting Up...
              </>
            ) : (
              'I Understand - Use Local Mode'
            )}
          </button>
        </div>
      </div>
    </div>
  )

  // Maintenance Modal Component (now inline to access parent scope)
  const maintenanceModal = getModalState().showMaintenanceModal ? ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-md w-full">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <AlertTriangle className="h-8 w-8 text-orange-500 mr-3" />
            <h2 className="text-xl font-bold text-gray-900">Maintenance Mode</h2>
          </div>
          <button
            onClick={() => {
              setModalState({ showMaintenanceModal: false })
              forceRender() // Trigger re-render
            }}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Modal Content */}
        <div className="p-6">
          <p className="text-gray-700 mb-6">
            {getModalState().maintenanceMsg}
          </p>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start">
              <Monitor className="h-5 w-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <h4 className="font-medium text-blue-800 mb-1">Use Offline Mode</h4>
                <p className="text-blue-700 text-sm">
                  You can continue using the app with local storage while we're under maintenance.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Modal Actions */}
        <div className="flex gap-3 p-6 border-t border-gray-200">
          <button
            onClick={() => {
              setModalState({ showMaintenanceModal: false })
              forceRender() // Trigger re-render
            }}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
          <button
            onClick={() => {
              setModalState({ showMaintenanceModal: false })
              setShowLocalModeModal(true)
              forceRender() // Trigger re-render
            }}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Use Offline Mode
          </button>
        </div>
      </div>
    </div>,
    document.body
  ) : null

  return (
    <div className="min-h-screen auth-gradient-bg flex flex-col justify-center py-6 sm:py-12 px-4 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        {/* Logo and Title */}
        <div className="flex justify-center items-center space-x-2 sm:space-x-3">
          <img src={noteSparkLogo} alt="NoteSpark Logo" className="h-10 w-10 sm:h-12 sm:w-12" />
          <span className="text-xl sm:text-2xl font-bold text-gray-900">notespark</span>
        </div>
        <h2 className="mt-4 sm:mt-6 text-center text-2xl sm:text-3xl font-normal text-gray-900">
          Sign in to your account
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Or{' '}
          <Link
            to="/register"
            className="font-medium text-indigo-600 hover:text-indigo-500"
          >
            create a new account
          </Link>
        </p>
      </div>

      <div className="mt-4 sm:mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-6 sm:py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <form className="space-y-4 sm:space-y-6" onSubmit={handleSubmit(onSubmit)}>
            {/* Global Error */}
            {errors.root && (
              <div className="rounded-md bg-red-50 p-4">
                <div className="text-sm text-red-700">
                  {errors.root.message}
                </div>
              </div>
            )}

            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email address
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  {...register('email', {
                    required: 'Email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Please enter a valid email address'
                    },
                    onChange: (e) => setEmailValue(e.target.value)
                  })}
                  type="email"
                  autoComplete="email"
                  className="appearance-none block w-full pl-12 pr-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 text-blue-600 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  placeholder="Enter your email"
                />
              </div>
              {errors.email && (
                <p className="mt-2 text-sm text-red-600">{errors.email.message}</p>
              )}
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  {...register('password', {
                    required: 'Password is required',
                    minLength: {
                      value: 8,
                      message: 'Password must be at least 8 characters'
                    }
                  })}
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  className="appearance-none block w-full pl-12 pr-10 py-2 border border-gray-300 rounded-md placeholder-gray-400 text-blue-600 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="mt-2 text-sm text-red-600">{errors.password.message}</p>
              )}
            </div>

            {/* Forgot Password Link */}
            <div className="text-right">
              <Link
                to="/forgot-password"
                className="text-sm text-indigo-600 hover:text-indigo-500"
              >
                Forgot your password?
              </Link>
            </div>

            {/* Submit Button */}
            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex justify-center items-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isLoading ? (
                  <>
                    <ButtonSpinner className="mr-2" />
                    Signing in...
                  </>
                ) : (
                  'Sign in'
                )}
              </button>
            </div>
          </form>

          {/* Local Mode Option */}
          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">Or</span>
              </div>
            </div>

            <div className="mt-6">
              <button
                type="button"
                onClick={handleLocalModeClick}
                className="w-full flex justify-center items-center py-2 px-4 border border-orange-300 rounded-md shadow-sm text-sm font-medium text-orange-700 bg-orange-50 hover:bg-orange-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition-colors"
              >
                <Monitor className="h-5 w-5 mr-2" />
                Use Locally (No Account Required)
              </button>
              <p className="mt-2 text-xs text-gray-500 text-center">
                Store notes in your browser only - no registration needed
              </p>
            </div>
          </div>


        </div>
      </div>

      {/* Local Mode Modal */}
      {showLocalModeModal && <LocalModeModal />}
      {/* Invalid Credentials Modal */}
      {invalidCredentialsModal}
      {/* Maintenance Modal */}
      {maintenanceModal}
    </div>
  )
}

export default LoginPage 